import type { AnyFieldApi } from "@tanstack/react-form";
import { useForm } from "@tanstack/react-form";
import type { QueryClient } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { PillBtn } from "#/components/primitives";
import { Field } from "#/components/ui/field";
import { Input } from "#/components/ui/input";
import { isEmailVerified, requestPasswordReset } from "#/lib/auth-client";
import { ROUTES } from "#/lib/routes";
import { sessionQueries } from "#/lib/useSession";

const AUTH_FIELD_CLASS = "h-14 px-6";

const forgotPasswordSchema = z.object({
  email: z.string().email("Enter a valid email"),
});

type ForgotPasswordValues = z.infer<typeof forgotPasswordSchema>;

function fieldErrorText(field: AnyFieldApi): string | null {
  if (!field.state.meta.isTouched) return null;
  const errs = field.state.meta.errors;
  if (errs.length === 0) return null;
  return errs
    .map((e) => (typeof e === "string" ? e : (e?.message ?? "Invalid")))
    .join(", ");
}

export async function forgotPasswordBeforeLoad({
  context,
}: {
  context: { queryClient: QueryClient };
}): Promise<void> {
  const session = await context.queryClient.ensureQueryData(
    sessionQueries.current(),
  );
  if (session.customer && isEmailVerified(session.customer)) {
    throw redirect({ to: ROUTES.account });
  }
}

export const Route = createFileRoute("/forgot-password")({
  head: () => ({ meta: [{ title: "Forgot Password" }] }),
  beforeLoad: forgotPasswordBeforeLoad,
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: ForgotPasswordValues) => requestPasswordReset(values),
    onSuccess: (_, values) => {
      setSubmittedEmail(values.email);
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof Error
          ? err.message
          : "Could not send reset link. Please try again.",
      );
    },
  });

  const form = useForm({
    defaultValues: { email: "" } satisfies ForgotPasswordValues,
    validators: { onChange: forgotPasswordSchema },
    onSubmit: ({ value }) => {
      setErrorMessage(null);
      return mutation.mutateAsync(value);
    },
  });

  return (
    <div data-screen-label="Forgot Password" className="pb-24">
      <section className="mx-auto w-full max-w-md px-6 pt-16 md:pt-24">
        <h1 className="m-0 mb-3 font-bold font-display text-4xl text-lp-green-dk leading-none tracking-tight">
          Forgot Password
        </h1>
        <p className="mt-2 mb-10 m-0 font-display text-lp-text">
          Enter the email tied to your account and we&rsquo;ll send a reset
          link.
        </p>

        {mutation.isSuccess && submittedEmail ? (
          <div className="rounded-3xl bg-lp-green-soft p-6 font-display text-lp-text-dk">
            If an account exists for <strong>{submittedEmail}</strong>, a reset
            link is on the way. Check your inbox.
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}
            className="flex flex-col gap-5"
            noValidate
          >
            <form.Field name="email">
              {(field) => {
                const errText = fieldErrorText(field);
                return (
                  <div>
                    <Field label="Email">
                      {(id) => (
                        <Input
                          id={id}
                          type="email"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          autoComplete="email"
                          aria-invalid={errText !== null}
                          className={AUTH_FIELD_CLASS}
                        />
                      )}
                    </Field>
                    {errText && (
                      <p
                        role="alert"
                        className="mt-1 font-display text-destructive text-sm"
                      >
                        {errText}
                      </p>
                    )}
                  </div>
                );
              }}
            </form.Field>

            {errorMessage && (
              <div className="font-display text-destructive text-sm">
                {errorMessage}
              </div>
            )}

            <div className="mt-2 flex justify-end">
              <form.Subscribe selector={(s) => [s.canSubmit, s.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <PillBtn
                    type="submit"
                    variant="green"
                    size="sm"
                    disabled={!canSubmit || isSubmitting || mutation.isPending}
                  >
                    {mutation.isPending || isSubmitting
                      ? "Sending…"
                      : "Send Reset Link"}
                  </PillBtn>
                )}
              </form.Subscribe>
            </div>
          </form>
        )}

        <div className="mt-10 font-display text-lp-text">
          <Link
            to={ROUTES.login}
            className="text-lp-green-dk underline hover:text-lp-green-mid"
          >
            Back to sign in
          </Link>
        </div>
      </section>
    </div>
  );
}
